// src/utils/safeInAppPath.ts
//
// The one check for "is this string a path on THIS site that is safe to navigate to?". Shared by the
// sign-in `?next=` redirect and the notification bell's server-provided links: anything the router is
// handed that turns out to be "//host" is passed to location.assign — another site.

// A backslash or any control character (tab, newline…): browsers normalise "/\evil.com" and
// "/<tab>/evil.com" to "//evil.com", a protocol-relative URL to another site.
// eslint-disable-next-line no-control-regex
const UNSAFE_CHARS = /[\\\u0000-\u001F\u007F]/;

/** "//host" or "/\host" — a browser treats either as a URL on another site. */
function isProtocolRelative(path: string): boolean {
    return path.startsWith('//') || path.startsWith('/\\');
}

/**
 * The normalised same-site path (pathname + search + hash) for `raw`, or null when it is not safe to
 * follow: another origin, a scheme (`javascript:`), a protocol-relative or backslash/control-char
 * trick, a dot segment that collapses into one ("/.//evil.com"), or any of those percent-encoded.
 */
export function safeInAppPath(raw: string | null | undefined): string | null {
    if (!raw || !raw.startsWith('/')) return null;

    let decoded: string;
    try {
        decoded = decodeURIComponent(raw);
    } catch {
        return null;
    }
    if (UNSAFE_CHARS.test(raw) || UNSAFE_CHARS.test(decoded) || decoded.startsWith('//')) return null;

    let url: URL;
    try {
        url = new URL(raw, window.location.origin);
    } catch {
        return null;
    }
    if (url.origin !== window.location.origin) return null;

    // Check the RESOLVED path, not just the input: dot segments ("/.//evil.com", "/a/..//evil.com",
    // "/%2e//evil.com") only collapse to a protocol-relative "//evil.com" during resolution.
    let resolvedPath: string;
    try {
        resolvedPath = decodeURIComponent(url.pathname);
    } catch {
        return null;
    }
    if (isProtocolRelative(url.pathname) || isProtocolRelative(resolvedPath) || UNSAFE_CHARS.test(resolvedPath)) {
        return null;
    }

    const result = url.pathname + url.search + url.hash;
    // Belt and braces: whatever happened above, never hand back something that leaves the site.
    return isProtocolRelative(result) ? null : result;
}

export function isSafeInAppPath(raw: string | null | undefined): boolean {
    return safeInAppPath(raw) !== null;
}
